import { z } from 'zod';
/**
 * Credential vault v1 contract (services/credential-vault, port 4725).
 *
 * The vault holds tenant credentials (mailbox refresh tokens, carrier, ERP and market provider credentials)
 * under authenticated encryption and serves them to other Trace services only. Callers hold an opaque
 * reference (`cred_...`), never the secret. It is a service-to-service API: every request carries a
 * service principal token for CREDENTIAL_VAULT_AUDIENCE, names the organization it acts for and states a
 * purpose, and the vault checks both against its access policies and audits the attempt.
 *
 * Every error body is a problem document (errors.ts). Secrets appear in exactly two places: the request
 * bodies of create and rotate, and the response of secret:read.
 */
/** Auth0 API identifier of the vault. Service tokens for any other audience are refused. */
export const CREDENTIAL_VAULT_AUDIENCE = 'urn:trace:api:credential-vault';
/**
 * Development service tokens: HMAC-signed tokens for local principals, accepted only by services running
 * with TRACE_ENV development or test, and refused at boot anywhere else. Their issuer and typ header label
 * them so they can never pass for an Auth0 token.
 */
export const DEV_SERVICE_TOKEN_ISSUER = 'urn:trace:dev-service-token';
export const DEV_SERVICE_TOKEN_TYPE = 'dev-service+jwt';
export const CREDENTIAL_KINDS = [
    'microsoft_graph_mailbox',
    'gmail_mailbox',
    'carrier_api',
    'erp',
    'market_provider',
];
export const credentialKindSchema = z.enum(CREDENTIAL_KINDS);
export const CREDENTIAL_STATUSES = ['active', 'revoked'];
export const credentialStatusSchema = z.enum(CREDENTIAL_STATUSES);
/** `cred_` and 128 random bits in lowercase hexadecimal. Opaque: it says nothing about the credential. */
export const CREDENTIAL_REF_PATTERN = /^cred_[0-9a-f]{32}$/;
export const credentialRefSchema = z
    .string()
    .regex(CREDENTIAL_REF_PATTERN, 'must be cred_ followed by 32 lowercase hexadecimal characters');
const LOWERCASE_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
/**
 * The Trace organization id. Only the canonical lowercase form is accepted: the id is bound into the
 * ciphertext's associated data, so two spellings of one organization must never exist.
 */
export const vaultOrganizationIdSchema = z.uuid().regex(LOWERCASE_UUID, 'must be a lowercase UUID');
/** Provider sub-key within a kind: a carrier slug, an ERP, a market data provider, a mail provider. */
export const vaultProviderSchema = z
    .string()
    .regex(/^[a-z0-9][a-z0-9_-]{0,63}$/, 'must be a lowercase machine name');
/** Why a caller wants access. Checked against the access policies and written to every audit row. */
export const purposeSchema = z
    .string()
    .regex(/^[a-z][a-z0-9_]{2,63}$/, 'must be a lowercase machine name of 3 to 64 characters');
/** A service principal: the client id of the calling service. */
export const principalSchema = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_.:@-]{0,127}$/);
export const KEY_ID_PATTERN = /^[a-z0-9][a-z0-9_-]{0,31}$/;
/** One version of a master key, by name. Never the key material. */
export const keyReferenceSchema = z.strictObject({
    id: z.string().regex(KEY_ID_PATTERN, 'must be a lowercase key id'),
    version: z.int().min(1).max(1_000_000),
});
export const SECRET_FIELD_NAME = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;
export const MAX_SECRET_FIELDS = 32;
export const MAX_SECRET_FIELD_LENGTH = 8192;
/** Upper bound of the serialized secret (UTF-8 bytes of its JSON form). */
export const MAX_SECRET_BYTES = 16 * 1024;
/**
 * The secret itself: named string fields, for example `{ "refreshToken": "..." }` or
 * `{ "apiKey": "...", "accountNumber": "..." }`. Validation messages never quote a value.
 */
export const secretFieldsSchema = z
    .record(z.string().regex(SECRET_FIELD_NAME, 'must be a field name'), z.string().min(1).max(MAX_SECRET_FIELD_LENGTH))
    .refine((fields) => {
    const count = Object.keys(fields).length;
    return count >= 1 && count <= MAX_SECRET_FIELDS;
}, `must hold between 1 and ${MAX_SECRET_FIELDS} fields`)
    .refine((fields) => new TextEncoder().encode(JSON.stringify(fields)).length <= MAX_SECRET_BYTES, `must be at most ${MAX_SECRET_BYTES} bytes once serialized`);
/**
 * Non-secret metadata, stored in plain columns apart from the ciphertext so it can be listed and shown.
 * Never put a secret here.
 */
export const credentialMetadataSchema = z.strictObject({
    /** A display label such as a mailbox address or "FedEx account ending 0042". */
    accountLabel: z.string().min(1).max(200).nullable().optional(),
    /** Granted scopes or permissions, for example OAuth scopes. */
    scopes: z
        .array(z.string().regex(/^[\x21-\x7e]{1,200}$/, 'must be printable without spaces'))
        .max(50)
        .optional(),
    /** When the secret stops working at its provider, if known. */
    expiresAt: z.iso.datetime({ offset: true }).nullable().optional(),
});
export const credentialMetadataViewSchema = z.object({
    accountLabel: z.string().nullable(),
    scopes: z.array(z.string()),
    expiresAt: z.iso.datetime().nullable(),
});
export const REVOCATION_REASONS = [
    'disconnected',
    'replaced',
    'compromised',
    'organization_offboarded',
    'other',
];
export const revocationReasonSchema = z.enum(REVOCATION_REASONS);
/**
 * POST /v1/credentials. `idempotencyKey` makes a retried create return the first credential instead of
 * storing the secret twice; the key is scoped to the calling principal.
 */
export const createCredentialRequestSchema = z.strictObject({
    organizationId: vaultOrganizationIdSchema,
    kind: credentialKindSchema,
    provider: vaultProviderSchema,
    purpose: purposeSchema,
    secret: secretFieldsSchema,
    metadata: credentialMetadataSchema.optional(),
    idempotencyKey: z
        .string()
        .regex(/^[A-Za-z0-9_-]{16,128}$/, 'must be 16 to 128 letters, digits, dashes or underscores')
        .optional(),
});
/** GET /v1/credentials/:ref query string. */
export const credentialQuerySchema = z.strictObject({
    organizationId: vaultOrganizationIdSchema,
    purpose: purposeSchema,
});
/** POST /v1/credentials/:ref/secret:read */
export const secretReadRequestSchema = z.strictObject({
    organizationId: vaultOrganizationIdSchema,
    purpose: purposeSchema,
});
/**
 * POST /v1/credentials/:ref/rotate. `expectedVersion` makes concurrent rotations safe: a rotation based
 * on a version that is no longer current is refused (412) instead of silently replacing a newer secret.
 */
export const rotateCredentialRequestSchema = z.strictObject({
    organizationId: vaultOrganizationIdSchema,
    purpose: purposeSchema,
    secret: secretFieldsSchema,
    metadata: credentialMetadataSchema.optional(),
    expectedVersion: z.int().min(1).optional(),
});
/** POST /v1/credentials/:ref/revoke. A fixed reason list, so no free text (or secret) lands in the audit. */
export const revokeCredentialRequestSchema = z.strictObject({
    organizationId: vaultOrganizationIdSchema,
    purpose: purposeSchema,
    reason: revocationReasonSchema,
});
/** A credential as other services see it: metadata only. */
export const credentialSchema = z.object({
    ref: credentialRefSchema,
    organizationId: z.string(),
    kind: credentialKindSchema,
    provider: z.string(),
    status: credentialStatusSchema,
    currentVersion: z.int().min(1),
    metadata: credentialMetadataViewSchema,
    createdAt: z.iso.datetime(),
    createdBy: z.string(),
    updatedAt: z.iso.datetime(),
    /** When the current version was written (the creation time until the first rotation). */
    rotatedAt: z.iso.datetime(),
    revokedAt: z.iso.datetime().nullable(),
    revocationReason: revocationReasonSchema.nullable(),
});
/** The answer of secret:read. Served with Cache-Control: no-store; never cache it. */
export const secretReadResponseSchema = z.object({
    ref: credentialRefSchema,
    organizationId: z.string(),
    kind: credentialKindSchema,
    provider: z.string(),
    version: z.int().min(1),
    secret: z.record(z.string(), z.string()),
    metadata: credentialMetadataViewSchema,
});
/**
 * POST /v1/admin/reencrypt (an ops principal only). Each call does a bounded slice of the job and records
 * its progress, so the caller repeats it until `job.status` is completed. `targetKey` must be the vault's
 * active key: a deploy that has not switched keys yet refuses instead of re-encrypting to the old one.
 */
export const reencryptRequestSchema = z.strictObject({
    purpose: purposeSchema,
    targetKey: keyReferenceSchema,
    /** Versions per batch (one transaction each), 100 by default. */
    batchSize: z.int().min(1).max(200).optional(),
    /** Batches per call, 10 by default: a call does at most 4,000 versions and then reports progress. */
    maxBatches: z.int().min(1).max(20).optional(),
});
/** `superseded`: the active key changed while the job ran; a job for the new key takes over. */
export const REENCRYPTION_JOB_STATUSES = ['running', 'completed', 'superseded'];
export const reencryptionJobSchema = z.object({
    id: z.uuid(),
    targetKey: keyReferenceSchema,
    status: z.enum(REENCRYPTION_JOB_STATUSES),
    processed: z.int().min(0),
    reencrypted: z.int().min(0),
    failed: z.int().min(0),
    startedAt: z.iso.datetime(),
    startedBy: z.string(),
    updatedAt: z.iso.datetime(),
    completedAt: z.iso.datetime().nullable(),
});
export const reencryptResponseSchema = z.object({
    job: reencryptionJobSchema,
    /** Versions still holding material under another key after this slice. */
    remaining: z.int().min(0),
});
/** GET /v1/admin/key-report query string. */
export const keyReportQuerySchema = z.strictObject({ purpose: purposeSchema });
/**
 * Which master key versions protect stored material (and whether this vault still holds each one), and
 * the credential versions not on the active key. A key may be retired once no material uses it.
 */
export const keyReportSchema = z.object({
    activeKey: keyReferenceSchema,
    keys: z.array(z.object({ key: keyReferenceSchema, versions: z.int().min(0), held: z.boolean() })),
    stale: z.array(z.object({ ref: credentialRefSchema, version: z.int().min(1), key: keyReferenceSchema })),
    staleCount: z.int().min(0),
    upToDate: z.boolean(),
});
/** Route table of the vault. The server registers exactly these paths; clients build them from here. */
export const CREDENTIAL_VAULT_API = {
    live: '/health/live',
    ready: '/health/ready',
    credentials: '/v1/credentials',
    credential: (ref) => `/v1/credentials/${encodeURIComponent(ref)}`,
    secretRead: (ref) => `/v1/credentials/${encodeURIComponent(ref)}/secret:read`,
    rotate: (ref) => `/v1/credentials/${encodeURIComponent(ref)}/rotate`,
    revoke: (ref) => `/v1/credentials/${encodeURIComponent(ref)}/revoke`,
    adminReencrypt: '/v1/admin/reencrypt',
    adminKeyReport: '/v1/admin/key-report',
};
//# sourceMappingURL=credential-vault.js.map